// 세션 목록 폴링 hook (Phase 2)
//
// backend GET /sql-developer/sessions 를 적응형 주기로 폴링:
//   - 어떤 세션이 running/cancelling 이면 1초
//   - 모두 idle 이면 3초
//   - **로그인이 끊겼으면(401) 간격을 두 배씩 늘린다 — 최대 5분**
// useEffect cleanup 으로 unmount/HMR 시 중단.
//
// 폴링 실패 시 list 는 그대로 두고 error 만 reducer 에 보관 (UX 안정).

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isSessionBusy } from '../utils/sessionDisplay';
import { fetchSessions } from '../redux/actions/sessionActions';
import { handleSessionExpired } from '../redux/actions/authActions';
import { detectMaintenance } from '../services/qbaseMaintenance';

const ACTIVE_INTERVAL = 1000;
const IDLE_INTERVAL = 3000;
/** 로그인이 끊긴 동안의 최대 간격. */
export const AUTH_BACKOFF_MAX = 5 * 60 * 1000;

/**
 * 다음 폴링까지의 간격.
 *
 * **401 에는 물러선다.** QBase 세션이 만료돼도 화면은 저장된 로그인 상태(redux-persist)로 남아, 이 폴링이
 * 3초마다 영원히 401 을 냈다 — 실측(2026-09-14): 탭 두 개가 사흘 동안 68,270건, 에러로그의 99.7% 가 이것
 * 하나였다. 보존 상한에 닿으면 다른 오류가 먼저 밀려난다. 그 밖의 실패(네트워크 등)는 지금처럼 3초 뒤 재시도.
 *
 * @param list         직전에 받은 세션 목록 (성공했을 때)
 * @param authFailures 연속된 401 횟수
 */
export function nextPollDelay({ list = [], authFailures = 0, failed = false } = {}) {
    if (authFailures > 0) return Math.min(AUTH_BACKOFF_MAX, IDLE_INTERVAL * (2 ** authFailures));
    if (failed) return IDLE_INTERVAL;
    return list.some(isSessionBusy) ? ACTIVE_INTERVAL : IDLE_INTERVAL;
}

const isAuthFailure = (err) => !!(err && err.response && err.response.status === 401);

export default function useSessionPolling(enabled = true) {
    const dispatch = useDispatch();
    // list 는 적응형 주기 결정에만 사용. 매 fetch 후 재평가.
    const sessions = useSelector(s => (s.sessions && s.sessions.list) || []);

    useEffect(() => {
        if (!enabled) return undefined;
        let cancelled = false;
        let timer = null;
        let inFlight = false;
        let authFailures = 0;

        const tick = async () => {
            if (cancelled || inFlight) return;
            inFlight = true;
            let delay;
            try {
                const fresh = await dispatch(fetchSessions());
                if (cancelled) return;
                authFailures = 0;
                delay = nextPollDelay({ list: fresh || [] });
            } catch (err) {
                // fetchSessions 가 dispatch SESSIONS_ERROR 함.
                if (isAuthFailure(err)) {
                    authFailures += 1;
                    // 로그인이 만료됐는지 QBase 에 확인한다 — 만료면 작업 화면 위에 로그인 창이 뜨고(인증 상태가
                    // 내려가) 이 폴링은 멈춘다. 확인이 "아니다" 면 물러선 간격으로 계속 본다.
                    dispatch(handleSessionExpired());
                } else {
                    authFailures = 0;
                    // QBase 점검(서버가 503 MAINTENANCE 로 넘긴다) — 만료가 아니다. 배너만 켠다
                    const r = err && err.response;
                    if (r && r.status === 503 && r.data && r.data.resultCode === 'MAINTENANCE') {
                        detectMaintenance(r.status, r.data, r.headers && r.headers['x-qbase-maintenance']);
                    }
                }
                delay = nextPollDelay({ authFailures, failed: true });
            } finally {
                inFlight = false;
            }
            if (!cancelled) {
                clearTimeout(timer);
                timer = setTimeout(tick, delay);
            }
        };

        // 물러서 있다가 사용자가 창으로 돌아오면 곧바로 다시 본다 — 다른 탭에서 다시 로그인했을 수 있다
        const onFocus = () => {
            if (authFailures === 0 || cancelled) return;
            clearTimeout(timer);
            tick();
        };
        window.addEventListener('focus', onFocus);

        // 초기 즉시 1회 + 이후 적응형
        tick();

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
            window.removeEventListener('focus', onFocus);
        };
        // sessions 의존성은 의도적으로 제거 — 상태가 바뀔 때마다 effect 재실행되면
        // 폴링 timer 가 매번 재생성되어 부담. tick 내부에서 매 회 적응형 주기 재계산.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, dispatch]);

    return { sessions };
}
